import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../UserContext';
import { Alert, Box, Button, Chip, Paper, Stack, Tab, TextField, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { getAllInactiveUsers, getAllUsers, getStats, runSyncDashboardsJob, runSyncOrgJob } from '../rest/BackendApi';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';
import ManageUsersTable from '../components/ManagerUsersTable';
import { getCurrentYearMonth, isValidYearMonth } from '../util/TimeUtil';
import { SimpleLoading } from '../components/Loading';
import { Stats } from '../types/StatsType';
import { PageContainer, PageHeader, Section } from '../components/Page';
import ConfirmDialog from '../components/ConfirmDialog';

type JobResult = { severity: 'success' | 'error'; text: string } | undefined;

export function describeJobResult(status: number | undefined, jobName: string): JobResult {
  if (status === undefined) return { severity: 'error', text: `${jobName} could not be started (network error).` };
  if (status >= 200 && status < 300) return { severity: 'success', text: `${jobName} started.` };
  return { severity: 'error', text: `${jobName} failed to start (HTTP ${status}).` };
}

function StatCard(props: { label: string; values: { label: string; value: number }[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {props.label}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {props.values.map(item => (
          <Box key={item.label}>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.value}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default function ManagePanel() {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const [loading, setLoading] = useState(true);
  const [allActiveUsers, setAllActiveUsers] = useState<User[] | null>(null);
  const [allInactiveUsers, setAllInactiveUsers] = useState<User[] | null>(null);
  const [stats, setStats] = useState<Stats | undefined>();
  const [tab, setTab] = useState('1');
  const [yearMonth, setYearMonth] = useState(getCurrentYearMonth());
  const [jobResult, setJobResult] = useState<JobResult>();
  const [runningJob, setRunningJob] = useState(false);
  const [confirmJob, setConfirmJob] = useState<'dashboards' | 'orgs' | null>(null);

  const load = async (action: () => Promise<void>) => {
    setLoading(true);
    try {
      await action();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab == '1' && allActiveUsers == null) {
      load(async () => setAllActiveUsers(await getAllUsers()));
    } else if (tab == '2' && allInactiveUsers == null) {
      load(async () => setAllInactiveUsers(await getAllInactiveUsers()));
    } else if (tab == '3' && !stats) {
      load(async () => setStats(await getStats()));
    } else {
      setLoading(false);
    }
  }, [tab]);

  const runJob = async (job: 'dashboards' | 'orgs') => {
    setRunningJob(true);
    setJobResult(undefined);
    const name = job === 'dashboards' ? `Dashboard sync for ${yearMonth}` : 'Organisation sync';
    let status: number | undefined;
    try {
      status = job === 'dashboards' ? await runSyncDashboardsJob(yearMonth) : await runSyncOrgJob();
    } catch (e) {
      status = undefined;
    }
    setJobResult(describeJobResult(status, name));
    setRunningJob(false);
  };

  if (!user || !user.isAdmin) {
    return null;
  }

  const loadingView = <SimpleLoading message="Loading" />;

  return (
    <PageContainer maxWidth="none">
      <ConfirmDialog
        title={confirmJob === 'dashboards' ? `Sync all dashboards for ${yearMonth}?` : 'Sync all organisations?'}
        open={confirmJob != null}
        setOpen={(open: boolean) => !open && setConfirmJob(null)}
        onConfirm={() => confirmJob && runJob(confirmJob)}
        cancelText="Cancel"
        confirmText="Run sync"
      >
        This runs for every firm and calls Xero for each connected organisation. It can take a while.
      </ConfirmDialog>

      <PageHeader
        title={
          <>
            Manage <Chip size="small" label="Admin" color="warning" variant="outlined" sx={{ verticalAlign: 'middle', ml: 1 }} />
          </>
        }
        description="Firms, usage and background jobs across all BAS Ready customers."
      />

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={(_, value: string) => setTab(value)} aria-label="Manage sections" variant="scrollable" allowScrollButtonsMobile>
            <Tab label="Active firms" value="1" />
            <Tab label="Inactive firms & users" value="2" />
            <Tab label="Stats" value="3" />
            <Tab label="Syncs" value="4" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ px: 0 }}>
          {loading || allActiveUsers == null ? loadingView : <ManageUsersTable users={allActiveUsers} />}
        </TabPanel>
        <TabPanel value="2" sx={{ px: 0 }}>
          {loading || allInactiveUsers == null ? loadingView : <ManageUsersTable users={allInactiveUsers} />}
        </TabPanel>
        <TabPanel value="3" sx={{ px: 0, maxWidth: 900 }}>
          {loading || !stats ? (
            loadingView
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <StatCard
                label="Emails sent"
                values={[
                  { label: 'Last 24 hours', value: stats.nbEmailsLast24h },
                  { label: 'Last 7 days', value: stats.nbEmailsLast7d },
                  { label: 'Last month', value: stats.nbEmailsLastMonth },
                ]}
              />
              <StatCard
                label="Users who logged in"
                values={[
                  { label: 'Last 24 hours', value: stats.loginLast24h },
                  { label: 'Last 7 days', value: stats.loginLast7d },
                  { label: 'Last month', value: stats.loginLastMonth },
                ]}
              />
            </Box>
          )}
        </TabPanel>
        <TabPanel value="4" sx={{ px: 0, maxWidth: 760 }}>
          {jobResult && (
            <Alert severity={jobResult.severity} sx={{ mb: 2 }} onClose={() => setJobResult(undefined)}>
              {jobResult.text}
            </Alert>
          )}
          <Section title="Sync dashboards" description="Re-run the dashboard sync for every firm for one month.">
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-start' }} gap={1.5}>
              <TextField
                size="small"
                value={yearMonth}
                onChange={e => setYearMonth(e.target.value)}
                label="Month"
                placeholder="YYYY-MM"
                error={!isValidYearMonth(yearMonth)}
                helperText={isValidYearMonth(yearMonth) ? ' ' : 'Use YYYY-MM, not in the future'}
              />
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                disabled={runningJob || !isValidYearMonth(yearMonth)}
                onClick={() => setConfirmJob('dashboards')}
              >
                Run dashboard sync
              </Button>
            </Stack>
          </Section>
          <Section title="Sync organisations" description="Refresh organisation details from Xero for every connected organisation.">
            <Button variant="outlined" startIcon={<SyncIcon />} disabled={runningJob} onClick={() => setConfirmJob('orgs')}>
              Run organisation sync
            </Button>
          </Section>
        </TabPanel>
      </TabContext>
    </PageContainer>
  );
}
